// src/layouts/TopBar/ApplicationSettings.jsx
// *******************************************************************************
// IMPORTS
// *******************************************************************************

import { Tooltip, Drawer, ConfigProvider, Segmented, Divider, Grid, Button, theme } from 'antd';
import { SettingOutlined, MoonOutlined, SunOutlined, ControlOutlined, RocketOutlined } from '@ant-design/icons';
import { ThemeSwitcherProvider, useThemeSwitcher } from 'react-css-theme-switcher';
import { DIR_RTL } from 'constants/ThemeConstant';
import StyledNavItem from './StyledNavItem';
import JarvislyCreditsWidget from './JarvislyCreditsWidget';
import translateX from 'utils/translateX';
import useZustand from 'hooks/useZustand';
import appService from 'services/appService';
import { darkTheme, lightTheme, baseTheme } from 'configs/appEngine';
import app from 'configs/appResolver';
import { Language } from './Language';
import ApplicationTour from 'layouts/Tour/ApplicationTour';

// *******************************************************************************
// CONSTANTS
// *******************************************************************************
const themes = {
  dark: `/css/dark-theme.css`,
  light: `/css/light-theme.css`,
};

const PREFERENCES_CATEGORY = 'appearance';

// *******************************************************************************
// DRAWER CONTENT COMPONENT
// *******************************************************************************
const DrawerContent = () => {
  // ─────────────────────────────────────────────────────────────────────────────
  // 1. PROPS, CONTEXTS & GLOBAL STATE
  // ─────────────────────────────────────────────────────────────────────────────
  const navType = useZustand('theme', 'navType');
  const currentTheme = useZustand('theme', 'currentTheme');
  const switchTheme = useZustand('theme', 'switchTheme');
  const setNavType = useZustand('theme', 'setNavType');
  const setHeaderNavColor = useZustand('theme', 'setHeaderNavColor');
  const setNavCollapsedStore = useZustand('theme', 'setNavCollapsed');
  const setTopNavColor = useZustand('theme', 'setTopNavColor');
  const storedSubscription = useZustand('auth', 'subscription');
  const storedOpenTour = useZustand('tour', 'openTour');
  const setSettingsDrawerOpen = useZustand('theme', 'setSettingsDrawerOpen');

  // ─────────────────────────────────────────────────────────────────────────────
  // 2. HOOKS & REFS
  // ─────────────────────────────────────────────────────────────────────────────
  const { switcher, themes: themeSwitcherThemes } = useThemeSwitcher();
  const { token } = theme.useToken();
  const screens = Grid.useBreakpoint();
  const isMobile = !screens.md;

  // ─────────────────────────────────────────────────────────────────────────────
  // 6. HANDLERS
  // ─────────────────────────────────────────────────────────────────────────────
  const handleThemeChange = async value => {
    setHeaderNavColor('');
    switchTheme(value);
    switcher({ theme: themeSwitcherThemes[value] });

    await appService.updateAppPreferences({
      category: 'appearance',
      data: { theme: value },
    });
  };

  const handleStartTour = () => {
    setSettingsDrawerOpen(false);
    setTimeout(() => {
      const TourComponent = props => <ApplicationTour {...props} />;
      storedOpenTour(TourComponent);
    }, 300);
  };

  const handleNavTypeChange = async value => {
    setHeaderNavColor('');
    if (value === 'top') {
      setTopNavColor(baseTheme.colorPrimary);
      setNavCollapsedStore(false);
    }
    setNavType(value);

    await appService.updateAppPreferences({
      category: 'appearance',
      data: { navigation: value },
    });
  };

  // ─────────────────────────────────────────────────────────────────────────────
  // 9. UI
  // ─────────────────────────────────────────────────────────────────────────────
  return (
    <>
      {/* Jarvisly Credits Section - Only show when logged in */}
      {storedSubscription?._id && (
        <>
          <div className="mb-4">
            <h4 className="mb-3 font-weight-bold" style={{ color: token.colorText }}>
              {translateX('jarvisly_credits')}
            </h4>
            <JarvislyCreditsWidget />
          </div>
          <Divider style={{ margin: '16px 0' }} />
        </>
      )}

      <div className="mb-5">
        <h4 className="mb-3 font-weight-bold" style={{ color: token.colorText }}>
          {translateX('preferences')}
        </h4>

        {storedSubscription?._id ? (
          <ListOption
            name={translateX('navigation_type')}
            selector={
              <Segmented
                size="small"
                value={navType}
                onChange={handleNavTypeChange}
                block
                style={{ minWidth: 140 }}
                options={[
                  { label: translateX('side'), value: 'side' },
                  { label: translateX('top'), value: 'top' },
                ]}
              />
            }
          />
        ) : null}

        <ListOption
          name={translateX('theme')}
          selector={
            <Segmented
              size="small"
              shape="round"
              value={currentTheme}
              onChange={handleThemeChange}
              options={[
                {
                  value: 'light',
                  icon: (
                    <Tooltip title={translateX('light')} placement="top">
                      <SunOutlined />
                    </Tooltip>
                  ),
                },
                {
                  value: 'dark',
                  icon: (
                    <Tooltip title={translateX('dark')} placement="top">
                      <MoonOutlined />
                    </Tooltip>
                  ),
                },
              ]}
            />
          }
        />

        {app?.LANGUAGE_OPTIONS?.length > 1 ? (
          <ListOption
            name={translateX('language')}
            selector={<Language configDisplay />}
          />
        ) : null}

        {isMobile && storedSubscription?._id && (
          <ListOption
            name={translateX('start_tour')}
            selector={
              <Button
                size="small"
                icon={<RocketOutlined />}
                onClick={handleStartTour}
              >
                {translateX('start')}
              </Button>
            }
          />
        )}
      </div>
    </>
  );
};

// *******************************************************************************
// DRAWER COMPONENT
// *******************************************************************************
const SettingsDrawer = () => {
  // ─────────────────────────────────────────────────────────────────────────────
  // 1. PROPS, CONTEXTS & GLOBAL STATE
  // ─────────────────────────────────────────────────────────────────────────────
  const direction = useZustand('theme', 'direction');
  const currentTheme = useZustand('theme', 'currentTheme');
  const settingsDrawerOpen = useZustand('theme', 'settingsDrawerOpen');
  const setSettingsDrawerOpen = useZustand('theme', 'setSettingsDrawerOpen');

  // ─────────────────────────────────────────────────────────────────────────────
  // 3. LOCAL VARIABLES I
  // ─────────────────────────────────────────────────────────────────────────────
  const drawerPlacement = direction === DIR_RTL ? 'left' : 'right';
  const themeConfig = currentTheme === 'light' ? { ...lightTheme } : { ...darkTheme };

  // ─────────────────────────────────────────────────────────────────────────────
  // 6. HANDLERS
  // ─────────────────────────────────────────────────────────────────────────────
  const handleCloseDrawer = () => setSettingsDrawerOpen(false);

  // ─────────────────────────────────────────────────────────────────────────────
  // 9. UI
  // ─────────────────────────────────────────────────────────────────────────────
  return (
    <ThemeSwitcherProvider
      themeMap={themes}
      defaultTheme={currentTheme || 'light'}
      insertionPoint="styles-insertion-point"
    >
      <ConfigProvider theme={themeConfig} direction={direction}>
        <Drawer
          title={translateX('application_configuration')}
          placement={drawerPlacement}
          size={350}
          onClose={handleCloseDrawer}
          open={settingsDrawerOpen}
          destroyOnHidden={false}
        >
          <DrawerContent />
        </Drawer>
      </ConfigProvider>
    </ThemeSwitcherProvider>
  );
};

// *******************************************************************************
// BUTTON COMPONENT
// *******************************************************************************
const SettingsButton = ({ mode }) => {
  // ─────────────────────────────────────────────────────────────────────────────
  // 1. PROPS, CONTEXTS & GLOBAL STATE
  // ─────────────────────────────────────────────────────────────────────────────
  const setSettingsDrawerOpen = useZustand('theme', 'setSettingsDrawerOpen');

  // ─────────────────────────────────────────────────────────────────────────────
  // 6. HANDLERS
  // ─────────────────────────────────────────────────────────────────────────────
  const handleOpenDrawer = () => setSettingsDrawerOpen(true);

  // ─────────────────────────────────────────────────────────────────────────────
  // 9. UI
  // ─────────────────────────────────────────────────────────────────────────────
  return (
    <div id="tour-settings">
      <Tooltip title={translateX('system_preferences')} placement="bottom">
        <StyledNavItem onClick={handleOpenDrawer} mode={mode}>
          <ControlOutlined className="nav-icon mr-0" />
          {/*<SettingOutlined className="nav-icon mr-0" />*/}
        </StyledNavItem>
      </Tooltip>
    </div>
  );
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export { SettingsButton, SettingsDrawer };
export default SettingsButton;

// *******************************************************************************
// HELPERS
// *******************************************************************************
const ListOption = ({ name, selector, disabled, vertical }) => (
  <div className={`my-4 ${vertical ? '' : 'd-flex align-items-center justify-content-between'}`}>
    <div className={`${disabled ? 'opacity-0-3' : ''} ${vertical ? 'mb-3' : ''}`}>{name}</div>
    <div>{selector}</div>
  </div>
);
